import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, resolveImageUrl } from '@/lib/axios';
import { Product, ProductCategory } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Eye, EyeOff, Edit2, Trash2, Tag, Image as ImageIcon, Sparkles, Upload, Star } from 'lucide-react';
import { toast } from 'sonner';

export default function ProductsAdminPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Partial<Product> | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [prodRes, catRes] = await Promise.all([
        api.get<Product[]>('/products?includeHidden=true'),
        api.get<ProductCategory[]>('/product-categories')
      ]);
      setProducts(Array.isArray(prodRes?.data) ? prodRes.data : []);
      setCategories(Array.isArray(catRes?.data) ? catRes.data : []);
    } catch (err) {
      setProducts([]);
      setCategories([]);
      toast.error('Error al cargar productos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePublic = async (product: Product) => {
    try {
      const updated = { ...product, isPublic: !product.isPublic };
      await api.put(`/products/${product.id}`, updated);
      setProducts(prev => prev.map(p => p.id === product.id ? updated : p));
      toast.success(updated.isPublic ? 'Producto visible en tienda' : 'Producto oculto de la tienda');
    } catch {
      toast.error('No se pudo actualizar visibilidad');
    }
  };

  const handleTogglePromo = async (product: Product) => {
    try {
      const updated = { ...product, isOnSale: !product.isOnSale };
      await api.put(`/products/${product.id}`, updated);
      setProducts(prev => prev.map(p => p.id === product.id ? updated : p));
      toast.success(updated.isOnSale ? 'Promoción activada' : 'Promoción desactivada');
    } catch {
      toast.error('No se pudo actualizar promoción');
    }
  };

  const handleToggleFeatured = async (product: Product) => {
    try {
      const updated = { ...product, isFeatured: !product.isFeatured };
      await api.put(`/products/${product.id}`, updated);
      setProducts(prev => prev.map(p => p.id === product.id ? updated : p));
      toast.success(updated.isFeatured ? '¡Producto destacado en Inicio!' : 'Producto quitado de destacados');
    } catch {
      toast.error('No se pudo actualizar destacado');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este producto del catálogo?')) return;
    try {
      await api.delete(`/products/${id}`);
      setProducts(prev => prev.filter(p => p.id !== id));
      toast.success('Producto eliminado');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct?.name || !editingProduct?.salePrice) {
      toast.error('Nombre y Precio de Venta son obligatorios');
      return;
    }

    try {
      const payload = {
        ...editingProduct,
        categoryId: editingProduct.categoryId || (categories[0]?.id ? Number(categories[0].id) : 1),
        costPrice: Number(editingProduct.costPrice || 0),
        salePrice: Number(editingProduct.salePrice || 0),
        promotionalPrice: editingProduct.promotionalPrice ? Number(editingProduct.promotionalPrice) : null,
        weightGrams: editingProduct.weightGrams ? Number(editingProduct.weightGrams) : null,
        printTimeHours: editingProduct.printTimeHours ? Number(editingProduct.printTimeHours) : null,
        images: editingProduct.images || '[]',
        variants: editingProduct.variants || '[]',
        estimatedDeliveryDays: Number(editingProduct.estimatedDeliveryDays) || 5,
        stockQuantity: Number(editingProduct.stockQuantity) || 0,
        manageStock: editingProduct.manageStock ?? true,
        allowBackorder: editingProduct.allowBackorder ?? true,
        isPublic: editingProduct.isPublic ?? true,
        isOnSale: editingProduct.isOnSale ?? false,
        isActive: true,
      };

      if (editingProduct.id) {
        await api.put(`/products/${editingProduct.id}`, payload);
        toast.success('Producto actualizado');
      } else {
        await api.post('/products', payload);
        toast.success('Producto creado');
      }
      setIsModalOpen(false);
      setEditingProduct(null);
      loadData();
    } catch {
      toast.error('Error al guardar el producto');
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Catálogo & Tienda</h1>
          <p className="text-muted-foreground text-sm">
            Administrá qué productos se muestran a los clientes, precios, fotos y promociones.
          </p>
        </div>
        <Button asChild className="gap-2">
          <Link to="/products-admin/new">
            <Plus className="h-4 w-4" /> Añadir Producto
          </Link>
        </Button>
      </div>

      {/* Grid de Productos */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Cargando catálogo...</div>
      ) : (!Array.isArray(products) || products.length === 0) ? (
        <Card className="p-8 text-center text-muted-foreground border-dashed">
          <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>No tenés productos cargados aún.</p>
          <p className="text-xs mt-1">Hacé clic en "Nuevo Producto" para agregar el primero.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(Array.isArray(products) ? products : []).map(product => (
            <Card key={product.id} className="overflow-hidden border border-border/70 flex flex-col justify-between hover:shadow-md transition-shadow">
              <div>
                {/* Imagen Preview */}
                <Link to={`/products-admin/${product.id}`} className="block relative aspect-video bg-muted flex items-center justify-center overflow-hidden hover:opacity-95 transition-opacity">
                  {product.mainImageUrl ? (
                    <img src={resolveImageUrl(product.mainImageUrl)} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-10 w-10 text-muted-foreground/40" />
                  )}
                  {/* Badges de estado */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    <Badge variant={product.isPublic ? "default" : "secondary"} className="gap-1 shadow-sm">
                      {product.isPublic ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      {product.isPublic ? 'En Tienda' : 'Oculto'}
                    </Badge>
                    {product.isFeatured && (
                      <Badge className="bg-amber-500 text-white shadow-sm gap-1 font-semibold">
                        <Star className="h-3 w-3 fill-white" /> Destacado
                      </Badge>
                    )}
                    {product.isOnSale && (
                      <Badge className="bg-destructive text-destructive-foreground shadow-sm gap-1">
                        <Tag className="h-3 w-3" /> Oferta
                      </Badge>
                    )}
                  </div>
                </Link>

                {/* Contenido */}
                <CardContent className="p-4 space-y-2">
                  <Link to={`/products-admin/${product.id}`}>
                    <h3 className="font-semibold text-lg line-clamp-1 hover:text-primary transition-colors cursor-pointer">
                      {product.name}
                    </h3>
                  </Link>
                  <p className="text-xs text-muted-foreground line-clamp-2 min-h-[32px]">
                    {product.shortDescription || product.description || 'Sin descripción.'}
                  </p>

                  {/* Precios */}
                  <div className="pt-2 flex items-baseline gap-2">
                    {product.isOnSale && product.promotionalPrice ? (
                      <>
                        <span className="text-xl font-bold text-primary">
                          ${product.promotionalPrice.toLocaleString('es-AR')}
                        </span>
                        <span className="text-sm line-through text-muted-foreground">
                          ${product.salePrice.toLocaleString('es-AR')}
                        </span>
                      </>
                    ) : (
                      <span className="text-xl font-bold text-foreground">
                        ${product.salePrice.toLocaleString('es-AR')}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">
                      Costo: ${product.costPrice?.toLocaleString('es-AR') || 0}
                    </span>
                  </div>

                  {/* Métricas taller */}
                  {(product.weightGrams || product.printTimeHours) && (
                    <div className="text-[11px] text-muted-foreground/80 flex gap-3 pt-1 border-t border-border/50">
                      {product.weightGrams && <span>⚖️ {product.weightGrams}g</span>}
                      {product.printTimeHours && <span>⏱️ {product.printTimeHours}hs</span>}
                    </div>
                  )}

                  {/* Stock físico y tiempo de entrega */}
                  <div className="flex items-center justify-between text-xs pt-2 border-t border-border/50">
                    <div className="flex items-center gap-1.5">
                      {product.stockQuantity > 0 ? (
                        <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] px-1.5 py-0 font-medium">
                          Stock: {product.stockQuantity} u.
                        </Badge>
                      ) : product.allowBackorder ? (
                        <Badge variant="outline" className="text-amber-600 border-amber-500/40 text-[10px] px-1.5 py-0 bg-amber-500/5">
                          A pedido ({product.estimatedDeliveryDays || 5}d)
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                          Agotado
                        </Badge>
                      )}
                    </div>
                    <span className="text-[11px] text-muted-foreground font-mono">
                      🚚 {product.estimatedDeliveryDays || 5}d
                    </span>
                  </div>
                </CardContent>
              </div>

              {/* Acciones */}
              <div className="p-3 bg-muted/20 border-t border-border/60 flex items-center justify-between gap-1 text-xs">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleTogglePublic(product)}
                  title={product.isPublic ? "Ocultar de la tienda" : "Mostrar en la tienda"}
                >
                  {product.isPublic ? <EyeOff className="h-4 w-4 mr-1 text-muted-foreground" /> : <Eye className="h-4 w-4 mr-1 text-primary" />}
                  {product.isPublic ? 'Ocultar' : 'Publicar'}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggleFeatured(product)}
                  className={product.isFeatured ? "text-amber-500 font-semibold" : "text-muted-foreground"}
                  title="Destacar en la portada principal"
                >
                  <Star className={`h-4 w-4 mr-1 ${product.isFeatured ? "fill-amber-500" : ""}`} />
                  Destacar
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleTogglePromo(product)}
                  className={product.isOnSale ? "text-destructive" : "text-muted-foreground"}
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Promo
                </Button>

                <div className="flex gap-1 ml-auto">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditingProduct(product);
                      setIsModalOpen(true);
                    }}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(product.id)}
                    className="hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal / Sheet de Creación y Edición */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <Card className="w-full max-w-xl max-h-[90vh] overflow-y-auto bg-card shadow-2xl border-border">
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <h2 className="text-xl font-bold">
                {editingProduct?.id ? 'Editar Producto' : 'Nuevo Producto'}
              </h2>

              <div className="grid gap-3">
                <div className="space-y-1">
                  <Label>Nombre del Producto *</Label>
                  <Input
                    required
                    value={editingProduct?.name || ''}
                    onChange={e => setEditingProduct({ ...editingProduct, name: e.target.value })}
                    placeholder="Ej. Lámpara Nómada E27"
                  />
                </div>

                <div className="space-y-1">
                  <Label>Categoría *</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
                    value={editingProduct?.categoryId || (categories[0]?.id ?? 1)}
                    onChange={e => setEditingProduct({ ...editingProduct, categoryId: Number(e.target.value) })}
                  >
                    {categories.length === 0 ? (
                      <option value="1">General</option>
                    ) : (
                      categories.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Precio de Venta ($) *</Label>
                    <Input
                      type="number"
                      required
                      value={editingProduct?.salePrice || ''}
                      onChange={e => setEditingProduct({ ...editingProduct, salePrice: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Costo Estimado Base ($)</Label>
                    <Input
                      type="number"
                      value={editingProduct?.costPrice || ''}
                      onChange={e => setEditingProduct({ ...editingProduct, costPrice: Number(e.target.value) })}
                    />
                  </div>
                </div>

                {/* Promoción */}
                <div className="p-3 bg-muted/40 rounded-xl space-y-2 border border-border/70">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold flex items-center gap-1.5 cursor-pointer">
                      <Sparkles className="h-4 w-4 text-primary" /> ¿Está en Oferta?
                    </Label>
                    <input
                      type="checkbox"
                      className="rounded h-4 w-4"
                      checked={editingProduct?.isOnSale || false}
                      onChange={e => setEditingProduct({ ...editingProduct, isOnSale: e.target.checked })}
                    />
                  </div>
                  {editingProduct?.isOnSale && (
                    <div className="space-y-1 pt-1">
                      <Label className="text-xs">Precio Promocional ($)</Label>
                      <Input
                        type="number"
                        placeholder="Ej. 12000 (menor al precio normal)"
                        value={editingProduct?.promotionalPrice || ''}
                        onChange={e => setEditingProduct({ ...editingProduct, promotionalPrice: Number(e.target.value) })}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Imagen del Producto</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      placeholder="URL o subí un archivo..."
                      value={editingProduct?.mainImageUrl || ''}
                      onChange={e => setEditingProduct({ ...editingProduct, mainImageUrl: e.target.value })}
                    />
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          try {
                            toast.loading('Subiendo imagen...');
                            const res = await api.post('/uploads', formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            toast.dismiss();
                            const fullUrl = resolveImageUrl(res.data.url);
                            setEditingProduct({ ...editingProduct, mainImageUrl: fullUrl });
                            toast.success('Imagen subida con éxito');
                          } catch {
                            toast.dismiss();
                            toast.error('Error al subir imagen');
                          }
                        }}
                      />
                      <Button type="button" variant="outline" asChild className="gap-1.5 shrink-0 pointer-events-none">
                        <span><Upload className="h-4 w-4" /> Subir</span>
                      </Button>
                    </label>
                  </div>
                  {editingProduct?.mainImageUrl && (
                    <div className="w-24 h-24 rounded-lg overflow-hidden border border-border mt-2 bg-muted">
                      <img src={resolveImageUrl(editingProduct.mainImageUrl)} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <Label>Descripción Breve</Label>
                  <Input
                    placeholder="Resumen para la tarjeta de producto"
                    value={editingProduct?.shortDescription || ''}
                    onChange={e => setEditingProduct({ ...editingProduct, shortDescription: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Gramos estimados (3D)</Label>
                    <Input
                      type="number"
                      placeholder="Ej. 180"
                      value={editingProduct?.weightGrams || ''}
                      onChange={e => setEditingProduct({ ...editingProduct, weightGrams: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Horas de Impresión</Label>
                    <Input
                      type="number"
                      step="0.5"
                      placeholder="Ej. 5.5"
                      value={editingProduct?.printTimeHours || ''}
                      onChange={e => setEditingProduct({ ...editingProduct, printTimeHours: Number(e.target.value) })}
                    />
                  </div>
                </div>

                {/* Inventario & Logística de Envío */}
                <div className="p-3.5 bg-muted/30 rounded-xl space-y-3 border border-border/70">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    📦 Inventario & Tiempo de Envío
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Stock Físico Inmediato (u.)</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={editingProduct?.stockQuantity ?? 0}
                        onChange={e => setEditingProduct({ ...editingProduct, stockQuantity: Number(e.target.value) })}
                      />
                      <p className="text-[10px] text-muted-foreground">Unidades listas para entrega inmediata</p>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Tiempo de Envío / Fabricación (días) *</Label>
                      <Input
                        type="number"
                        min="1"
                        placeholder="5"
                        value={editingProduct?.estimatedDeliveryDays ?? 5}
                        onChange={e => setEditingProduct({ ...editingProduct, estimatedDeliveryDays: Number(e.target.value) })}
                      />
                      <p className="text-[10px] text-muted-foreground">Días hábiles para entrega o fabricación</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-1 border-t border-border/40">
                    <input
                      type="checkbox"
                      id="allowBackorderCheck"
                      className="rounded h-4 w-4"
                      checked={editingProduct?.allowBackorder ?? true}
                      onChange={e => setEditingProduct({ ...editingProduct, allowBackorder: e.target.checked })}
                    />
                    <Label htmlFor="allowBackorderCheck" className="text-xs cursor-pointer leading-tight">
                      Permitir compra bajo demanda cuando no haya stock físico
                    </Label>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="isPublicCheck"
                    className="rounded h-4 w-4"
                    checked={editingProduct?.isPublic ?? true}
                    onChange={e => setEditingProduct({ ...editingProduct, isPublic: e.target.checked })}
                  />
                  <Label htmlFor="isPublicCheck" className="cursor-pointer">
                    Mostrar inmediatamente en la tienda pública
                  </Label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingProduct(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingProduct?.id ? 'Guardar Cambios' : 'Crear Producto'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
